import {
    MarginalTaxRateCalculator,
    RetirementAccountType,
} from '@fruitful/financial-calculators';
import { roundToTwoDecimals, transformEnumToDisplay } from '@fruitful/fruitils';
import { TaxFiling } from '@fruitful/types';
import React from 'react';
import {
    type Control,
    type FieldErrors,
    useFieldArray,
    type UseFormRegister,
    type UseFormSetValue,
    type UseFormWatch,
} from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    ControlledPicker,
    H5Emphasized,
    H6Emphasized,
    IconButton,
    LabelRegular,
    Picker,
    PlusIcon,
    RotateCcwIcon,
    TrashIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { MutationErrorToastOptions } from '../../common/components/Toasts';
import { US_STATES } from '../../common/constants/States';
import type { RetirementCalculatorFormData } from '../types';

interface RetirementCalculatorHouseholdInputsProps {
    control: Control<RetirementCalculatorFormData>;
    register: UseFormRegister<RetirementCalculatorFormData>;
    errors: FieldErrors<RetirementCalculatorFormData>;
    watch: UseFormWatch<RetirementCalculatorFormData>;
    setValue: UseFormSetValue<RetirementCalculatorFormData>;
    memberIds: string[];
}

const TAX_FILING_OPTIONS = Object.values(TaxFiling).map((taxFiling) => ({
    label: transformEnumToDisplay(taxFiling),
    value: taxFiling,
}));

const RETIREMENT_ACCOUNT_TYPE_OPTIONS = Object.values(
    RetirementAccountType,
).map((type) => ({
    label: transformEnumToDisplay(type),
    value: type,
}));

export const RetirementCalculatorHouseholdInputs = ({
    control,
    register,
    errors,
    watch,
    setValue,
    memberIds,
}: RetirementCalculatorHouseholdInputsProps) => {
    const toast = useToastController();

    // Waterfall input state and logic
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'householdInputs.retirementContributionWaterfall',
    });

    const memberOptions = memberIds.map((id, index) => ({
        label: `${index === 0 ? 'Primary Member' : 'Partner Member'} (${id})`,
        value: id,
    }));

    const addWaterfallStep = () => {
        append({
            memberId: memberIds[0] ?? '',
            contributions: {},
        });
    };

    const addContribution = (stepIndex: number) => {
        const currentStep = watch(
            `householdInputs.retirementContributionWaterfall.${stepIndex}`,
        );

        // Find the first available account type that's not already used
        const usedAccountTypes = Object.keys(currentStep?.contributions ?? {});
        const availableAccountType = RETIREMENT_ACCOUNT_TYPE_OPTIONS.find(
            (option) => !usedAccountTypes.includes(option.value),
        );

        if (availableAccountType != null && currentStep != null) {
            const updatedContributions = {
                ...currentStep.contributions,
                [availableAccountType.value]: '0', // Initialize with 0 for whole number percentage
            };
            setValue(
                `householdInputs.retirementContributionWaterfall.${stepIndex}.contributions`,
                updatedContributions,
            );
        }
    };

    const removeContribution = (stepIndex: number, accountType: string) => {
        const currentStep = watch(
            `householdInputs.retirementContributionWaterfall.${stepIndex}`,
        );
        if (currentStep?.contributions == null) {
            return;
        }
        const { [accountType]: removed, ...updatedContributions } =
            currentStep.contributions;

        setValue(
            `householdInputs.retirementContributionWaterfall.${stepIndex}.contributions`,
            updatedContributions,
        );
    };

    const calculateMarginalTaxRate = () => {
        const formData = watch();

        // Get required data for tax rate calculation
        const grossAnnualIncome =
            parseFloat(
                formData.primaryMember?.currentGrossAnnualIncome ?? '0',
            ) +
            parseFloat(formData.partnerMember?.currentGrossAnnualIncome ?? '0');

        const residentialLocation =
            formData.householdInputs.residentialLocation;
        const taxFilingStatus = formData.householdInputs
            .taxFilingStatus as TaxFiling;

        try {
            const taxRateCalculator = new MarginalTaxRateCalculator(
                grossAnnualIncome,
                residentialLocation,
                taxFilingStatus,
            );
            const calculatedRate = taxRateCalculator.calculate();

            // Convert to percentage display and set the field
            setValue(
                'householdInputs.currentMarginalTaxRate',
                roundToTwoDecimals(calculatedRate * 100).toString(),
            );
        } catch (error) {
            toast.show(
                'Error calculating tax rate',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <YStack gap={16}>
            <H5Emphasized>Household Data</H5Emphasized>

            <ControlledInput
                label="Annual Spending in Retirement ($)"
                name="householdInputs.annualSpendingInRetirement"
                control={control}
                register={register}
                errors={errors}
                type="numeric"
            />

            <ControlledInput
                label="Target Success Likelihood (%)"
                name="householdInputs.targetSuccessLikelihood"
                control={control}
                register={register}
                errors={errors}
                type="numeric"
            />

            <ControlledPicker
                label="Tax Filing Status"
                name="householdInputs.taxFilingStatus"
                control={control}
                register={register}
                errors={errors}
                options={TAX_FILING_OPTIONS}
            />

            <ControlledPicker
                label="Residential State"
                name="householdInputs.residentialLocation"
                control={control}
                register={register}
                errors={errors}
                options={[
                    ...US_STATES,
                    { label: 'United States', value: 'US' },
                ]}
            />

            <XStack gap={8} alignItems="center">
                <ControlledInput
                    label="Current Marginal Tax Rate (%)"
                    name="householdInputs.currentMarginalTaxRate"
                    control={control}
                    register={register}
                    errors={errors}
                    type="numeric"
                    containerStyle={{
                        flex: 1,
                    }}
                />
                <IconButton
                    variant="secondary"
                    icon={RotateCcwIcon}
                    onPress={calculateMarginalTaxRate}
                />
            </XStack>

            {/* Retirement Contribution Waterfall Input */}
            <YStack gap={12}>
                <H6Emphasized>Retirement Contribution Waterfall</H6Emphasized>

                {fields.map((field, stepIndex) => {
                    const currentStep = watch(
                        `householdInputs.retirementContributionWaterfall.${stepIndex}`,
                    );
                    const contributionEntries = Object.entries(
                        currentStep?.contributions ?? {},
                    );

                    return (
                        <YStack
                            key={field.id}
                            gap={12}
                            padding={16}
                            backgroundColor="$gray100"
                            borderRadius={8}
                        >
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <H6Emphasized>
                                    Step {stepIndex + 1}
                                </H6Emphasized>
                                <Button
                                    width="auto"
                                    text="Remove Step"
                                    icon={TrashIcon}
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    tone="danger"
                                    onPress={() => {
                                        remove(stepIndex);
                                    }}
                                />
                            </XStack>

                            <ControlledPicker
                                label="Member"
                                name={`householdInputs.retirementContributionWaterfall.${stepIndex}.memberId`}
                                control={control}
                                register={register}
                                errors={errors}
                                options={memberOptions}
                            />

                            <YStack gap={12}>
                                <H6Emphasized>
                                    Account Contributions
                                </H6Emphasized>

                                {contributionEntries.length === 0 && (
                                    <YStack
                                        padding={8}
                                        backgroundColor="$gray200"
                                        borderRadius={4}
                                    >
                                        <LabelRegular>
                                            No account contributions defined
                                        </LabelRegular>
                                    </YStack>
                                )}

                                {contributionEntries.map(([accountType, _]) => {
                                    return (
                                        <XStack key={accountType} gap={8}>
                                            <YStack gap={12} flex={1}>
                                                <LabelRegular>
                                                    Account Type
                                                </LabelRegular>
                                                <Picker
                                                    value={accountType}
                                                    onChange={(
                                                        newAccountType,
                                                    ) => {
                                                        if (
                                                            newAccountType !==
                                                            accountType
                                                        ) {
                                                            // Update the contributions object while preserving order
                                                            const currentContributions =
                                                                currentStep?.contributions ??
                                                                {};

                                                            // Reconstruct object in same order, replacing the old key with new key
                                                            const updatedContributions: Record<
                                                                string,
                                                                string
                                                            > = {};
                                                            Object.entries(
                                                                currentContributions,
                                                            ).forEach(
                                                                ([
                                                                    key,
                                                                    value,
                                                                ]) => {
                                                                    if (
                                                                        key ===
                                                                        accountType
                                                                    ) {
                                                                        updatedContributions[
                                                                            newAccountType
                                                                        ] =
                                                                            value;
                                                                    } else {
                                                                        updatedContributions[
                                                                            key
                                                                        ] =
                                                                            value;
                                                                    }
                                                                },
                                                            );

                                                            setValue(
                                                                `householdInputs.retirementContributionWaterfall.${stepIndex}.contributions`,
                                                                updatedContributions,
                                                            );
                                                        }
                                                    }}
                                                    options={RETIREMENT_ACCOUNT_TYPE_OPTIONS.filter(
                                                        (option) =>
                                                            option.value ===
                                                                (accountType as RetirementAccountType) ||
                                                            !Object.keys(
                                                                currentStep?.contributions ??
                                                                    {},
                                                            ).includes(
                                                                option.value,
                                                            ),
                                                    )}
                                                />
                                            </YStack>
                                            <ControlledInput
                                                label="Percentage (%)"
                                                name={`householdInputs.retirementContributionWaterfall.${stepIndex}.contributions.${accountType}`}
                                                control={control}
                                                register={register}
                                                errors={errors}
                                                type="numeric"
                                                containerStyle={{ flex: 1 }}
                                            />
                                            <IconButton
                                                variant="secondary"
                                                icon={TrashIcon}
                                                onPress={() => {
                                                    removeContribution(
                                                        stepIndex,
                                                        accountType,
                                                    );
                                                }}
                                                tone="danger"
                                            />
                                        </XStack>
                                    );
                                })}

                                <Button
                                    text="Add Contribution"
                                    icon={PlusIcon}
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    disabled={
                                        contributionEntries.length >=
                                        RETIREMENT_ACCOUNT_TYPE_OPTIONS.length
                                    }
                                    onPress={() => {
                                        addContribution(stepIndex);
                                    }}
                                />
                            </YStack>
                        </YStack>
                    );
                })}

                <Button
                    text="Add Waterfall Step"
                    icon={PlusIcon}
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    onPress={addWaterfallStep}
                />
            </YStack>
        </YStack>
    );
};
